import { useState, useCallback, useEffect } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
	Box,
	Button,
	Paper,
	Typography,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
	MenuItem,
	Alert,
	IconButton,
	Chip,
	Stack,
	Switch,
	FormControlLabel
} from '@mui/material';
import {
	DataGrid,
	GridColDef,
	GridActionsCellItem,
	GridPaginationModel,
	GridRowParams,
	GridEventListener
} from '@mui/x-data-grid';
import { Autocomplete } from '@mui/material';
import {
	Add as AddIcon,
	Edit as EditIcon,
	Delete as DeleteIcon,
	VpnKey as VpnKeyIcon,
	Refresh as RefreshIcon,
	ArrowBack as ArrowBackIcon,
	Block as BlockIcon,
	LockOpen as LockOpenIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PasswordGenerator } from '../../components/Auth/PasswordGenerator';
import { validateForm, getFieldError } from '../../schemas/forms';
import {
	UserManagementSchema,
	CreateUserSchema,
	UpdateUserSchema,
	ResetPasswordSchema,
	type UserManagement,
	type CreateUserInput,
	type UpdateUserInput,
	type FacultyOption,
	type UserFormData,
} from '../../schemas/userManagement.schema';
import { PaginatedApiResponseSchema } from '../../schemas';
import { z } from 'zod';
import { AxiosError } from 'axios';
import { apiClient } from '../../lib/api';
import { useUser } from '../../lib/auth-client';
import { MOBILE_BREAKPOINT, FULLSCREEN_DIALOG_PAPER_SX, FULLSCREEN_DIALOG_CONTENT_SX, MOBILE_CELL_STYLES, MOBILE_GUIDE } from '../../utils/responsive';

const fetchFaculties = async (): Promise<FacultyOption[]> => {
	const response = await apiClient.get<{ data: FacultyOption[] }>('/faculties', { limit: 1000 });
	return response.data.data || [];
};

const fetchUsers = async (page: number, limit: number, search?: string) => {
	const params = new URLSearchParams({
		page: page.toString(),
		limit: limit.toString(),
		...(search && { search })
	});

	const response = await apiClient.get(`/admin/users?${params}`);
	return PaginatedApiResponseSchema(z.array(UserManagementSchema)).parse(response.data);
};

const createUser = async (userData: CreateUserInput) => {
	const response = await apiClient.post('/admin/users', userData);
	return response.data;
};

const updateUser = async ({ id, data }: { id: string; data: UpdateUserInput }) => {
	const response = await apiClient.put(`/admin/users/${id}`, data);
	return response.data;
};

const deleteUser = async (id: string) => {
	const response = await apiClient.delete(`/admin/users/${id}`);
	return response.data;
};

const resetPassword = async ({ id, password }: { id: string; password: string }) => {
	const response = await apiClient.post(`/admin/users/${id}/reset-password`, { password });
	return response.data;
};

type DialogType = 'create' | 'edit' | 'delete' | 'reset-password' | null;

export default function AccountManagementPage() {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down(MOBILE_BREAKPOINT));
	const [searchParams, setSearchParams] = useSearchParams();
	const navigate = useNavigate();
	const { user: currentUser } = useUser();
	const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
		page: 0,
		pageSize: 20
	});
	const [search, setSearch] = useState('');
	const [dialogType, setDialogType] = useState<DialogType>(null);
	const [selectedUser, setSelectedUser] = useState<UserManagement | null>(null);
	const [formData, setFormData] = useState<UserFormData>({});
	const [validationErrors, setValidationErrors] = useState<z.ZodError | undefined>(undefined);
	const [successMessage, setSuccessMessage] = useState<string>('');
	const [errorMessage, setErrorMessage] = useState<string>('');

	const queryClient = useQueryClient();

	const { data: facultiesData } = useQuery({
		queryKey: ['faculties-list'],
		queryFn: fetchFaculties,
		staleTime: 5 * 60 * 1000
	});

	const { data, isLoading } = useQuery({
		queryKey: ['users', paginationModel.page + 1, paginationModel.pageSize, search],
		queryFn: () => fetchUsers(paginationModel.page + 1, paginationModel.pageSize, search)
	});

	// Sync URL params with dialog state (only on mobile)
	useEffect(() => {
		if (!isMobile) return;

		const action = searchParams.get('action') as DialogType;
		const userId = searchParams.get('id');

		if (action && ['create', 'edit', 'reset-password'].includes(action)) {
			if (action === 'create') {
				setDialogType('create');
				setFormData({
					username: '',
					email: '',
					name: '',
					password: '',
					role: 'user',
					maKhoa: '',
					MaCB: ''
				});
				setValidationErrors(undefined);
			} else if (userId && data?.data) {
				const user = data.data.find((u) => u.id === userId);
				if (user) {
					setSelectedUser(user);
					setDialogType(action);
					if (action === 'edit') {
						setFormData({
							email: user.email || '',
							name: user.name,
							role: (user.role || 'user') as UserFormData['role'],
							writable: user.writable || false,
							maKhoa: user.maKhoa || '',
							MaCB: user.MaCB || '',
							banned: user.banned || false,
							banReason: user.banReason || '',
							banExpires: user.banExpires || ''
						});
					} else if (action === 'reset-password') {
						setFormData({ password: '' });
					}
					setValidationErrors(undefined);
				}
			}
		} else if (!action) {
			setDialogType(null);
			setSelectedUser(null);
		}
	}, [searchParams, isMobile, data?.data]);

	const createMutation = useMutation({
		mutationFn: createUser,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['users'] });
			setSuccessMessage('Tạo tài khoản thành công!');
			handleCloseDialog();
		},
		onError: (error: Error) => {
			const axiosErr = error as AxiosError<{ message?: string }>;
			setErrorMessage(axiosErr.response?.data?.message || 'Tạo tài khoản thất bại');
		}
	});

	const updateMutation = useMutation({
		mutationFn: updateUser,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['users'] });
			setSuccessMessage('Cập nhật tài khoản thành công!');
			handleCloseDialog();
		},
		onError: (error: Error) => {
			const axiosErr = error as AxiosError<{ message?: string }>;
			setErrorMessage(axiosErr.response?.data?.message || 'Cập nhật tài khoản thất bại');
		}
	});

	const deleteMutation = useMutation({
		mutationFn: deleteUser,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['users'] });
			setSuccessMessage('Xóa tài khoản thành công!');
			handleCloseDialog();
		},
		onError: (error: Error) => {
			const axiosErr = error as AxiosError<{ message?: string }>;
			setErrorMessage(axiosErr.response?.data?.message || 'Xóa tài khoản thất bại');
		}
	});

	const resetPasswordMutation = useMutation({
		mutationFn: resetPassword,
		onSuccess: () => {
			setSuccessMessage('Đặt lại mật khẩu thành công!');
			handleCloseDialog();
		},
		onError: (error: Error) => {
			const axiosErr = error as AxiosError<{ message?: string }>;
			setErrorMessage(axiosErr.response?.data?.message || 'Đặt lại mật khẩu thất bại');
		}
	});

	const handleOpenCreateDialog = useCallback(() => {
		if (isMobile) {
			setSearchParams({ action: 'create' });
		} else {
			setDialogType('create');
			setFormData({
				username: '',
				email: '',
				name: '',
				password: '',
				role: 'user',
				maKhoa: '',
				MaCB: ''
			});
			setValidationErrors(undefined);
		}
	}, [isMobile, setSearchParams]);

	const handleOpenEditDialog = useCallback((user: UserManagement) => {
		if (isMobile) {
			setSearchParams({ action: 'edit', id: user.id });
		} else {
			setDialogType('edit');
			setSelectedUser(user);
			setFormData({
				email: user.email || '',
				name: user.name,
				role: (user.role || 'user') as UserFormData['role'],
				writable: user.writable || false,
				maKhoa: user.maKhoa || '',
				MaCB: user.MaCB || '',
				banned: user.banned || false,
				banReason: user.banReason || '',
				banExpires: user.banExpires || ''
			});
			setValidationErrors(undefined);
		}
	}, [isMobile, setSearchParams]);

	const handleToggleBan = useCallback((user: UserManagement) => {
		if (user.id === currentUser?.id) return;
		updateMutation.mutate({
			id: user.id,
			data: user.banned
				? { banned: false, banReason: null, banExpires: null }
				: { banned: true, banReason: null, banExpires: null }
		});
	}, [currentUser?.id, updateMutation]);

	const handleOpenDeleteDialog = useCallback((user: UserManagement) => {
		setDialogType('delete');
		setSelectedUser(user);
	}, []);

	const handleOpenResetPasswordDialog = useCallback((user: UserManagement) => {
		if (isMobile) {
			setSearchParams({ action: 'reset-password', id: user.id });
		} else {
			setDialogType('reset-password');
			setSelectedUser(user);
			setFormData({ password: '' });
			setValidationErrors(undefined);
		}
	}, [isMobile, setSearchParams]);

	const handleCloseDialog = useCallback(() => {
		if (isMobile && searchParams.has('action')) {
			navigate(-1);
		} else {
			setDialogType(null);
			setSelectedUser(null);
			setFormData({});
			setValidationErrors(undefined);
		}
	}, [isMobile, searchParams, navigate]);

	const handleCreate = useCallback(() => {
		const validation = validateForm(CreateUserSchema, formData);

		if (!validation.success) {
			setValidationErrors(validation.errors);
			return;
		}

		setValidationErrors(undefined);
		if (validation.data) {
			const dataToSubmit: CreateUserInput = {
				...validation.data,
				role: validation.data.role || 'user'
			};
			createMutation.mutate(dataToSubmit);
		}
	}, [formData, createMutation]);

	const handleUpdate = useCallback(() => {
		if (!selectedUser) return;

		const validation = validateForm(UpdateUserSchema, formData);

		if (!validation.success) {
			setValidationErrors(validation.errors);
			return;
		}

		setValidationErrors(undefined);
		if (validation.data) {
			updateMutation.mutate({ id: selectedUser.id, data: validation.data });
		}
	}, [selectedUser, formData, updateMutation]);

	const handleDelete = useCallback(() => {
		if (!selectedUser) return;
		deleteMutation.mutate(selectedUser.id);
	}, [selectedUser, deleteMutation]);

	const handleResetPassword = useCallback(() => {
		if (!selectedUser) return;

		const validation = validateForm(ResetPasswordSchema, formData);

		if (!validation.success) {
			setValidationErrors(validation.errors);
			return;
		}

		setValidationErrors(undefined);
		if (validation.data) {
			resetPasswordMutation.mutate({
				id: selectedUser.id,
				password: validation.data.password
			});
		}
	}, [selectedUser, formData, resetPasswordMutation]);

	const handleRowClick: GridEventListener<'rowClick'> = useCallback((params) => {
		const user = params.row as UserManagement;
		const isSelf = user.id === currentUser?.id;
		const isTargetAdmin = user.role === 'admin';
		const canEditTarget = !isTargetAdmin || isSelf;
		if (canEditTarget) {
			handleOpenEditDialog(user);
		}
	}, [handleOpenEditDialog, currentUser?.id]);

	const roleColors: Record<string, 'default' | 'primary' | 'error'> = {
		admin: 'error',
		manager: 'primary',
		user: 'default'
	};
	const roleLabels: Record<string, string> = {
		admin: 'Admin',
		manager: 'Quản lý',
		user: 'Người dùng'
	};

	const columns: GridColDef[] = isMobile
		? [
			{
				field: 'name',
				headerName: 'Tài khoản',
				minWidth: MOBILE_CELL_STYLES.MIN_WIDTH,
				flex: 1,
				sortable: true,
				filterable: false,
				renderCell: ({ row }) => {
					const user = row as UserManagement;
					return (
						<Box
							sx={{
								width: '100%',
								height: '100%',
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'flex-start',
								justifyContent: 'center',
								padding: MOBILE_CELL_STYLES.PADDING,
								whiteSpace: 'normal',
								wordWrap: 'break-word',
								lineHeight: MOBILE_CELL_STYLES.LINE_HEIGHT,
								gap: MOBILE_CELL_STYLES.GAP,
							}}
						>
							<Typography variant="caption" color="text.secondary">
								@{user.username}
							</Typography>
							<Typography variant="body2" fontWeight="bold">
								{user.name}
							</Typography>
							<Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" gap={0.5}>
								<Chip
									label={user.role ? (roleLabels[user.role] || user.role) : 'N/A'}
									color={user.role ? (roleColors[user.role] || 'default') : 'default'}
									size="small"
									sx={{ fontSize: '0.7rem', height: 20 }}
								/>
								<Chip
									label={user.banned ? 'Bị khóa' : 'Hoạt động'}
									color={user.banned ? 'error' : 'success'}
									size="small"
									sx={{ fontSize: '0.7rem', height: 20 }}
								/>
								{user.maKhoa && (
									<Chip
										label={user.maKhoa}
										variant="outlined"
										size="small"
										sx={{ fontSize: '0.7rem', height: 20 }}
									/>
								)}
							</Stack>
							<Typography variant="caption" color={MOBILE_GUIDE.COLOR} fontWeight={MOBILE_GUIDE.FONT_WEIGHT}>
								{MOBILE_GUIDE.TEXT}
							</Typography>
						</Box>
					);
				},
			},
		]
		: [
			{
				field: 'username',
				headerName: 'Tên đăng nhập',
				flex: 1,
				minWidth: 150
			},
			{
				field: 'name',
				headerName: 'Họ và tên',
				flex: 1,
				minWidth: 200
			},
			{
				field: 'email',
				headerName: 'Email',
				flex: 1,
				minWidth: 200,
				renderCell: (params) => params.value || '(không có)'
			},
			{
				field: 'role',
				headerName: 'Vai trò',
				width: 120,
				renderCell: (params) => (
					<Chip
						label={roleLabels[params.value] || params.value}
						color={roleColors[params.value] || 'default'}
						size="small"
					/>
				)
			},
			{
				field: 'writable',
				headerName: 'Quyền chỉnh sửa',
				width: 130,
				renderCell: (params) => {
					const row = params.row as UserManagement;
					if (row.role !== 'manager') return '-';
					return (
						<Chip
							label={params.value ? 'Có' : 'Không'}
							color={params.value ? 'success' : 'default'}
							size="small"
						/>
					);
				}
			},
			{
				field: 'maKhoa',
				headerName: 'Mã khoa',
				width: 100,
				renderCell: (params) => params.value || '-'
			},
			{
				field: 'banned',
				headerName: 'Trạng thái',
				width: 120,
				renderCell: (params) => (
					<Chip
						label={params.value ? 'Bị khóa' : 'Hoạt động'}
						color={params.value ? 'error' : 'success'}
						size="small"
					/>
				)
			},
			{
				field: 'actions',
				type: 'actions',
				headerName: 'Thao tác',
				width: 150,
				getActions: (params: GridRowParams<UserManagement>) => {
					const isSelf = params.row.id === currentUser?.id;
					const isTargetAdmin = params.row.role === 'admin';
					const canEditTarget = !isTargetAdmin || isSelf;
					const canDeleteTarget = !isSelf && !isTargetAdmin;
					const canBanTarget = !isSelf;
					const isBanned = !!params.row.banned;

					return [
						<GridActionsCellItem
							icon={<EditIcon />}
							label="Chỉnh sửa"
							onClick={() => handleOpenEditDialog(params.row)}
							disabled={!canEditTarget}
						/>,
						isBanned ? (
							<GridActionsCellItem
								icon={<LockOpenIcon color={canBanTarget ? 'success' : undefined} />}
								label="Mở khóa"
								onClick={() => handleToggleBan(params.row)}
								disabled={!canBanTarget || updateMutation.isPending}
							/>
						) : (
							<GridActionsCellItem
								icon={<BlockIcon color={canBanTarget ? 'warning' : undefined} />}
								label="Khóa tài khoản"
								onClick={() => handleToggleBan(params.row)}
								disabled={!canBanTarget || updateMutation.isPending}
							/>
						),
						<GridActionsCellItem
							icon={<VpnKeyIcon />}
							label="Đặt lại mật khẩu"
							onClick={() => handleOpenResetPasswordDialog(params.row)}
						/>,
						<GridActionsCellItem
							icon={<DeleteIcon />}
							label="Xóa"
							onClick={() => handleOpenDeleteDialog(params.row)}
							disabled={!canDeleteTarget}
							showInMenu
						/>
					];
				}
			}
		];

	return (
		<Box sx={{ p: { xs: 2, sm: 3 } }}>
			<Stack
				direction={{ xs: 'column', sm: 'row' }}
				justifyContent="space-between"
				alignItems={{ xs: 'stretch', sm: 'center' }}
				spacing={2}
				mb={3}
			>
				<Typography
					variant="h4"
					component="h1"
					sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' } }}
				>
					Quản lý tài khoản
				</Typography>
				<Button
					variant="contained"
					startIcon={<AddIcon />}
					onClick={handleOpenCreateDialog}
					sx={{ width: { xs: '100%', sm: 'auto' } }}
				>
					Tạo tài khoản mới
				</Button>
			</Stack>

			{successMessage && (
				<Alert
					severity="success"
					onClose={() => setSuccessMessage('')}
					sx={{ mb: 2 }}
				>
					{successMessage}
				</Alert>
			)}

			{errorMessage && (
				<Alert
					severity="error"
					onClose={() => setErrorMessage('')}
					sx={{ mb: 2 }}
				>
					{errorMessage}
				</Alert>
			)}

			<Paper sx={{ mb: 2, p: { xs: 1.5, sm: 2 } }}>
				<Stack
					direction="row"
					spacing={{ xs: 1, sm: 2 }}
					alignItems="center"
				>
					<TextField
						placeholder="Tìm kiếm theo tên đăng nhập, tên, email..."
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						size="small"
						sx={{ flexGrow: 1 }}
					/>
					<IconButton onClick={() => queryClient.invalidateQueries({ queryKey: ['users'] })}>
						<RefreshIcon />
					</IconButton>
				</Stack>
			</Paper>

			<Paper sx={{ overflowX: 'auto' }}>
				<DataGrid
					rows={data?.data || []}
					columns={columns}
					getRowId={(row) => row.id}
					rowCount={data?.pagination?.total || 0}
					loading={isLoading}
					pageSizeOptions={[10, 20, 50, 100]}
					paginationModel={paginationModel}
					paginationMode="server"
					onPaginationModelChange={setPaginationModel}
					onRowClick={handleRowClick}
					disableRowSelectionOnClick
					autoHeight
					getRowHeight={() => (isMobile ? 'auto' : 52)}
					sx={{
						cursor: 'pointer',
						'& .MuiDataGrid-footerContainer': {
							overflow: 'hidden',
						},
						'& .MuiTablePagination-root': {
							overflow: 'hidden',
						},
						'& .MuiDataGrid-cell:focus': {
							outline: 'none'
						},
						'& .MuiDataGrid-columnHeaders': {
							minHeight: { xs: '48px !important', sm: '56px !important' }
						},
						'& .MuiDataGrid-cell': {
							minHeight: { xs: '48px !important', sm: '52px !important' },
							py: isMobile ? 1 : 0,
							alignItems: isMobile ? 'flex-start' : 'center'
						},
						'& .MuiDataGrid-row': {
							minHeight: isMobile ? '80px !important' : '52px !important'
						}
					}}
				/>
			</Paper>

			{/* Create Dialog */}
			<Dialog
				open={dialogType === 'create'}
				onClose={handleCloseDialog}
				maxWidth="sm"
				fullWidth
				fullScreen={isMobile}
				PaperProps={isMobile ? { sx: FULLSCREEN_DIALOG_PAPER_SX } : undefined}
			>
				<DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
					{isMobile && (
						<IconButton
							aria-label="back"
							onClick={handleCloseDialog}
							edge="start"
							sx={{ mr: 1 }}
						>
							<ArrowBackIcon />
						</IconButton>
					)}
					Tạo tài khoản mới
				</DialogTitle>
				<DialogContent sx={isMobile ? FULLSCREEN_DIALOG_CONTENT_SX : undefined}>
					<Stack spacing={2} sx={{ mt: 1 }}>
						<TextField
							label="Username"
							value={formData.username || ''}
							onChange={(e) => setFormData({ ...formData, username: e.target.value })}
							error={!!getFieldError(validationErrors, 'username')}
							helperText={getFieldError(validationErrors, 'username')}
							required
							fullWidth
						/>
						<TextField
							label="Email"
							type="email"
							value={formData.email || ''}
							onChange={(e) => setFormData({ ...formData, email: e.target.value })}
							error={!!getFieldError(validationErrors, 'email')}
							helperText={getFieldError(validationErrors, 'email') || 'Email là tùy chọn'}
							fullWidth
						/>
						<TextField
							label="Họ và tên"
							value={formData.name || ''}
							onChange={(e) => setFormData({ ...formData, name: e.target.value })}
							error={!!getFieldError(validationErrors, 'name')}
							helperText={getFieldError(validationErrors, 'name')}
							required
							fullWidth
						/>
						<PasswordGenerator
							value={formData.password || ''}
							onChange={(password) => setFormData({ ...formData, password })}
							label="Mật khẩu"
						/>
						<TextField
							label="Vai trò"
							select
							value={formData.role || 'user'}
							onChange={(e) => setFormData({ ...formData, role: e.target.value as UserFormData['role'], writable: e.target.value === 'manager' ? formData.writable : false })}
							fullWidth
						>
							<MenuItem value="user">Người dùng</MenuItem>
							<MenuItem value="manager">Quản lý</MenuItem>
							<MenuItem value="admin">Admin</MenuItem>
						</TextField>
						{formData.role === 'manager' && (
							<FormControlLabel
								control={
									<Switch
										checked={formData.writable || false}
										onChange={(e) => setFormData({ ...formData, writable: e.target.checked })}
									/>
								}
								label="Quyền chỉnh sửa giới hạn"
							/>
						)}
						<Autocomplete
							options={facultiesData || []}
							getOptionLabel={(option: FacultyOption) => `${option.maKhoa} - ${option.tenKhoa}`}
							value={facultiesData?.find((f: FacultyOption) => f.maKhoa === formData.maKhoa) || null}
							onChange={(_, newValue: FacultyOption | null) => setFormData({ ...formData, maKhoa: newValue?.maKhoa || '' })}
							renderInput={(params) => (
								<TextField
									{...params}
									label="Mã khoa"
									placeholder="Chọn khoa (tùy chọn)"
								/>
							)}
							fullWidth
						/>
						<TextField
							label="Mã cán bộ"
							value={formData.MaCB || ''}
							onChange={(e) => setFormData({ ...formData, MaCB: e.target.value })}
							fullWidth
						/>
					</Stack>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCloseDialog}>Hủy</Button>
					<Button
						onClick={handleCreate}
						variant="contained"
						disabled={createMutation.isPending}
					>
						{createMutation.isPending ? 'Đang tạo...' : 'Tạo tài khoản'}
					</Button>
				</DialogActions>
			</Dialog>

			{/* Edit Dialog */}
			<Dialog
				open={dialogType === 'edit'}
				onClose={handleCloseDialog}
				maxWidth="sm"
				fullWidth
				fullScreen={isMobile}
				PaperProps={isMobile ? { sx: FULLSCREEN_DIALOG_PAPER_SX } : undefined}
			>
				<DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
					{isMobile && (
						<IconButton
							aria-label="back"
							onClick={handleCloseDialog}
							edge="start"
							sx={{ mr: 1 }}
						>
							<ArrowBackIcon />
						</IconButton>
					)}
					Chỉnh sửa tài khoản
				</DialogTitle>
				<DialogContent sx={isMobile ? FULLSCREEN_DIALOG_CONTENT_SX : undefined}>
					<Stack spacing={2} sx={{ mt: 1 }}>
						<TextField
							label="Username"
							value={selectedUser?.username || ''}
							disabled
							fullWidth
							helperText="Username không thể thay đổi"
						/>
						<TextField
							label="Email"
							type="email"
							value={formData.email || ''}
							onChange={(e) => setFormData({ ...formData, email: e.target.value })}
							error={!!getFieldError(validationErrors, 'email')}
							helperText={getFieldError(validationErrors, 'email')}
							fullWidth
						/>
						<TextField
							label="Họ và tên"
							value={formData.name || ''}
							onChange={(e) => setFormData({ ...formData, name: e.target.value })}
							error={!!getFieldError(validationErrors, 'name')}
							helperText={getFieldError(validationErrors, 'name')}
							required
							fullWidth
						/>
						<TextField
							label="Vai trò"
							select
							value={formData.role || 'user'}
							onChange={(e) => setFormData({ ...formData, role: e.target.value as UserFormData['role'], writable: e.target.value === 'manager' ? formData.writable : false })}
							disabled={selectedUser?.role === 'admin' && selectedUser?.id !== currentUser?.id}
							fullWidth
							helperText={
								selectedUser?.role === 'admin' && selectedUser?.id !== currentUser?.id
									? 'Không thể thay đổi vai trò của admin khác'
									: selectedUser?.id === currentUser?.id && selectedUser?.role === 'admin'
										? 'Không thể hạ cấp vai trò của chính mình'
										: ''
							}
						>
							<MenuItem value="user">Người dùng</MenuItem>
							<MenuItem value="manager">Quản lý</MenuItem>
							<MenuItem value="admin">Admin</MenuItem>
						</TextField>
						<FormControlLabel
							control={
								<Switch
									checked={formData.writable || false}
									onChange={(e) => setFormData({ ...formData, writable: e.target.checked })}
									disabled={formData.role !== 'manager'}
								/>
							}
							label="Quyền chỉnh sửa giới hạn"
						/>
						<Autocomplete
							options={facultiesData || []}
							getOptionLabel={(option: FacultyOption) => `${option.maKhoa} - ${option.tenKhoa}`}
							value={facultiesData?.find((f: FacultyOption) => f.maKhoa === formData.maKhoa) || null}
							onChange={(_, newValue: FacultyOption | null) => setFormData({ ...formData, maKhoa: newValue?.maKhoa || '' })}
							renderInput={(params) => (
								<TextField
									{...params}
									label="Mã khoa"
									placeholder="Chọn khoa (tùy chọn)"
								/>
							)}
							fullWidth
						/>
						<TextField
							label="Mã cán bộ"
							value={formData.MaCB || ''}
							onChange={(e) => setFormData({ ...formData, MaCB: e.target.value })}
							fullWidth
						/>
					</Stack>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCloseDialog}>Hủy</Button>
					<Button
						onClick={handleUpdate}
						variant="contained"
						disabled={updateMutation.isPending}
					>
						{updateMutation.isPending ? 'Đang cập nhật...' : 'Cập nhật'}
					</Button>
				</DialogActions>
			</Dialog>

			{/* Delete Confirmation Dialog - always modal overlay */}
			<Dialog
				open={dialogType === 'delete'}
				onClose={handleCloseDialog}
				maxWidth="sm"
			>
				<DialogTitle>
					Xác nhận xóa tài khoản
				</DialogTitle>
				<DialogContent>
					<Typography>
						Bạn có chắc chắn muốn xóa tài khoản <strong>{selectedUser?.username}</strong> ({selectedUser?.name})?
					</Typography>
					<Alert severity="warning" sx={{ mt: 2 }}>
						Hành động này không thể hoàn tác!
					</Alert>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCloseDialog}>Hủy</Button>
					<Button
						onClick={handleDelete}
						variant="contained"
						color="error"
						disabled={deleteMutation.isPending}
					>
						{deleteMutation.isPending ? 'Đang xóa...' : 'Xóa tài khoản'}
					</Button>
				</DialogActions>
			</Dialog>

			{/* Reset Password Dialog */}
			<Dialog
				open={dialogType === 'reset-password'}
				onClose={handleCloseDialog}
				maxWidth="sm"
				fullWidth
				fullScreen={isMobile}
				PaperProps={isMobile ? { sx: FULLSCREEN_DIALOG_PAPER_SX } : undefined}
			>
				<DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
					{isMobile && (
						<IconButton
							aria-label="back"
							onClick={handleCloseDialog}
							edge="start"
							sx={{ mr: 1 }}
						>
							<ArrowBackIcon />
						</IconButton>
					)}
					Đặt lại mật khẩu
				</DialogTitle>
				<DialogContent sx={isMobile ? FULLSCREEN_DIALOG_CONTENT_SX : undefined}>
					<Typography variant="body2" sx={{ mb: 2 }}>
						Đặt lại mật khẩu cho tài khoản <strong>{selectedUser?.username}</strong> ({selectedUser?.name})
					</Typography>
					<PasswordGenerator
						value={formData.password || ''}
						onChange={(password) => setFormData({ ...formData, password })}
						label="Mật khẩu (tùy chọn)"
					/>
					<Typography variant="caption" color="text.secondary" sx={{ mt: -1, mb: 1 }}>
						Để trống nếu muốn tạo tài khoản không có mật khẩu (OAuth only)
					</Typography>
					{getFieldError(validationErrors, 'password') && (
						<Alert severity="error" sx={{ mt: 2 }}>
							{getFieldError(validationErrors, 'password')}
						</Alert>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCloseDialog}>Hủy</Button>
					<Button
						onClick={handleResetPassword}
						variant="contained"
						disabled={resetPasswordMutation.isPending}
					>
						{resetPasswordMutation.isPending ? 'Đang đặt lại...' : 'Đặt lại mật khẩu'}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
}
